export const NOTE_TEMPLATE_CLIENT = `1. Chief Complaint

   - 'I'm worried about what they're going to think about me because of everything that's happened to me.'

2. Mental Status Examination

   - Speech: Normal rate, rhythm, and volume.
   - Mood: "I'm doing better."
   - Affect: Restricted range, appropriate, and flat quality.
   - Thought Content: No delusions, no suicidal or homicidal ideation, preoccupied with self-worth.
   - Thought Process: Logical and goal-directed.
   - Associations: Intact.
   - Perceptions: No hallucinations reported.
   - Judgment: Fair; evidenced by seeking employment and reconnecting with family.
   - Insight: Fair; recognizes improvement and ongoing challenges.
   - Cognition: Grossly intact.
   - Orientation: Alert and oriented to person, place, time, and situation.

3. History of Present Illness and Presenting Issues

   - [Name] is a 50-year-old male presenting with concerns about social interactions and self-worth following a period of unemployment and depression.
   - Context: Recently reconnected with family and sought additional work hours.
   - Current Situation: Actively seeking employment and managing social anxiety.
   - Key Themes:
     1. Employment Anxiety: Concerns about explaining employment gaps.
     2. Social Reconnection: Reconnecting with family and friends.
     3. Self-Worth: Struggles with self-perception and confidence.

4. Strengths

   - Demonstrates resilience by actively seeking employment and reconnecting with family. Shows willingness to engage in therapeutic exercises and role-playing to improve social interactions.

5. Limitations

   - Experiences anxiety related to social interactions and employment history. Displays a tendency to assume negative judgments from others, impacting self-esteem.

6. Interventions/Assessment

   - Engaged in cognitive restructuring to address negative automatic thoughts.
   - Practiced role-playing to enhance social confidence.
   - Reinforced the use of action plans and worksheets to track progress.

7. Engagement and Tone

   - The patient participated actively in the session, demonstrating openness to therapeutic exercises.
   - Receptive to feedback and willing to explore new strategies for managing anxiety and self-worth.

8. Risk Assessment

   - Risk Score: Low
   - Rationale: No current suicidal or homicidal ideation; actively engaged in treatment and goal-setting.
   - Progress Summary: Patient shows improvement in mood and engagement with therapeutic activities.

9. Patient's Next Steps/Goals

   - Continue applying for jobs with a focus on customer service.
   - Attend daughter's barbecue and practice social engagement strategies.
   - Maintain a list of personal achievements to reinforce self-worth.

10. Overall Next Steps

   1. Continue cognitive-behavioral therapy sessions to address social anxiety and self-worth.
   2. Encourage ongoing participation in family activities to strengthen social connections.
   3. Next session scheduled in one week per usual on this particular date/time.`;

export const NOTE_TEMPLATE_PATIENT = `Chief Complaint
"Chest pain for 3 days"

History of Present Illness
   - 58-year-old male presenting with substernal chest pain, started 3 days ago, pressure-like quality, 7/10 severity, radiating to left arm, associated with dyspnea and diaphoresis. Worse with exertion, partially relieved with rest. No prior episodes. Denies nausea, vomiting, or syncope. Patient states pain woke him from sleep this morning.

Past Medical History
   - Hypertension (diagnosed 2019)
   - Type 2 Diabetes Mellitus (diagnosed 2021)
   - Hyperlipidemia (diagnosed 2020)

Past Surgical History
   -Appendectomy (1995)
   -Right inguinal hernia repair (2018)

Medications
   -Metformin 1000mg PO BID
   -Lisinopril 10mg PO daily
   -Atorvastatin 40mg PO daily
   -Aspirin 81mg PO daily

Allergies
   - Penicillin (rash)

Social History
   - Tobacco: Former smoker, quit 2020, 20 pack-year history
   - Alcohol: Social, 1-2 drinks weekly
   - Occupation: Accountant, sedentary work

Family History
   - Father: MI at age 62, HTN
   - Mother: Type 2 DM, alive
   - Siblings: Brother with hyperlipidemia

Review of Systems
   - Constitutional: Denies fever, chills, weight loss
   - Cardiovascular: Positive for chest pain and dyspnea as above
   - Respiratory: Denies cough, hemoptysis
   - GI: Denies abdominal pain, nausea, vomiting
   - Neuro: Denies headache, dizziness, focal weakness

Physical Examination
   - Vitals: BP 145/92, HR 88, RR 18, Temp 98.6°F, SpO2 97% on RA
   - General: Alert, oriented, mild distress
   - HEENT: PERRLA, EOMI, oropharynx clear
   - Cardiovascular: Regular rate and rhythm, no murmurs/rubs/gallops, no JVD
   - Pulmonary: Clear to auscultation bilaterally, no wheezes/rales
   - Abdomen: Soft, non-tender, non-distended, normal bowel sounds
   - Extremities: No edema, pulses 2+ bilaterally
   - Neuro: CN II-XII intact, strength 5/5 all extremities

Assessment & Plan

1. Acute coronary syndrome

   - Order ECG, serial troponins, CXR, BMP, CBC
   - Start aspirin 325mg, nitroglycerin PRN
   - Cardiology consult
   - Telemetry monitoring

2. Hypertension, uncontrolled

   - Continue lisinopril, consider dose adjustment pending cardiac workup

3. Type 2 Diabetes Mellitus

   - Continue metformin, check HbA1c
   - Glucose monitoring while inpatient
`;

export const INTRUCTIONS_FOR_PROMPT_PLACEHOLDER = `e.g., Instructions:

1. If the patient has a cardiometabolic disease such as hypertension, hyperlipidemia, diabetes, or obesity calculate a 10 year ASCVD risk score if the data is available.
2. Add a patient friendly after visit summary at the end of the assessment and plan
3. If the patient is a smoker add a short, evidence-based smoking cessation counseling summary, including typical first-line pharmacologic and non-pharmacologic options, even if patient is not yet ready to quit.
`;
